import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Sparkles, Search, Upload, Play, Settings, TrendingUp, Star, DollarSign,
  Plus, Trash2, Loader2, CheckCircle, AlertCircle, RefreshCw, Zap
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDiscovery = () => {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [sources, setSources] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Import state
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [feedType, setFeedType] = useState('csv');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  
  // Config dialog
  const [configOpen, setConfigOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  
  // Source dialog
  const [sourceOpen, setSourceOpen] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', source_type: 'csv', url: '' });
  
  // Bulk AI generation
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, configRes, sourcesRes, suppliersRes, jobsRes] = await Promise.all([
        axios.get(`${API}/admin/discovery/stats`),
        axios.get(`${API}/admin/discovery/config`),
        axios.get(`${API}/admin/discovery/sources`),
        axios.get(`${API}/admin/suppliers`),
        axios.get(`${API}/admin/discovery/jobs?limit=5`)
      ]);
      setStats(statsRes.data);
      setConfig(configRes.data);
      setSources(sourcesRes.data.sources);
      setSuppliers(suppliersRes.data.suppliers);
      setJobs(jobsRes.data.jobs);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!feedUrl) {
      toast.error('Inserisci URL del feed');
      return;
    }
    
    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const response = await axios.post(`${API}/admin/discovery/preview`, null, {
        params: { feed_url: feedUrl, feed_type: feedType }
      });
      setPreviewData(response.data);
    } catch (error) {
      toast.error('Errore durante l\'anteprima');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleAutoImport = async () => {
    if (!selectedSupplier) {
      toast.error('Seleziona un fornitore');
      return;
    }
    if (!feedUrl) {
      toast.error('Inserisci URL del feed');
      return;
    }
    
    setImporting(true);
    
    try {
      const response = await axios.post(`${API}/admin/discovery/auto-import`, null, {
        params: {
          supplier_id: selectedSupplier,
          feed_url: feedUrl,
          feed_type: feedType
        }
      });
      
      toast.success(`Importazione completata: ${response.data.imported} prodotti`, {
        description: `Top: ${response.data.breakdown?.top_selected || 0}, Trend: ${response.data.breakdown?.trend_selected || 0}, Budget: ${response.data.breakdown?.budget_selected || 0}`
      });
      
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setImporting(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/admin/discovery/config`, editConfig);
      toast.success('Configurazione salvata');
      setConfigOpen(false);
      setConfig(editConfig);
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleAddSource = async () => {
    if (!newSource.name || !newSource.url) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/discovery/sources`, newSource);
      toast.success('Fonte aggiunta');
      setSourceOpen(false);
      setNewSource({ name: '', source_type: 'csv', url: '' });
      fetchAll();
    } catch (error) {
      toast.error('Errore nell\'aggiunta');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (!window.confirm('Eliminare questa fonte?')) return;
    
    try {
      await axios.delete(`${API}/admin/discovery/sources/${sourceId}`);
      toast.success('Fonte eliminata');
      fetchAll();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleBulkGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/discovery/bulk-generate-ai`, null, {
        params: { limit: 50, language: 'it', only_missing: true }
      });
      toast.success(`AI generata per ${response.data.generated} prodotti`);
    } catch (error) {
      toast.error('Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'top': return 'bg-amber-100 text-amber-700';
      case 'trend': return 'bg-purple-100 text-purple-700';
      case 'budget': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-discovery">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-serif">Product Discovery</h1>
          <p className="text-muted-foreground mt-1">
            Auto-import intelligente con selezione Top, Trend e Convenienti
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditConfig(config); setConfigOpen(true); }}>
            <Settings className="h-4 w-4 mr-2" />
            Configura
          </Button>
          <Button onClick={handleBulkGenerate} disabled={generating}>
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
            Genera AI Bulk
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className={stats?.needs_import ? 'border-amber-300 bg-amber-50' : ''}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Prodotti Totali</p>
                <p className="text-3xl font-bold">{stats?.total_products || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Target: {stats?.target_min_products || 2000}
                </p>
              </div>
              {stats?.needs_import && (
                <Badge className="bg-amber-500">+{stats.products_needed} necessari</Badge>
              )}
            </div>
            {stats && (
              <Progress
                value={(stats.total_products / stats.target_min_products) * 100}
                className="mt-4"
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <Star className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prodotti TOP</p>
                <p className="text-2xl font-bold">{stats?.by_type?.top || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prodotti TREND</p>
                <p className="text-2xl font-bold">{stats?.by_type?.trend || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prodotti BUDGET</p>
                <p className="text-2xl font-bold">{stats?.by_type?.budget || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="import" className="space-y-6">
        <TabsList>
          <TabsTrigger value="import">Auto-Import</TabsTrigger>
          <TabsTrigger value="sources">Fonti Salvate</TabsTrigger>
          <TabsTrigger value="history">Storico Import</TabsTrigger>
        </TabsList>

        {/* Auto-Import Tab */}
        <TabsContent value="import" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Import Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Import Intelligente
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Fornitore *</Label>
                  <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                    <SelectTrigger data-testid="select-supplier-discovery">
                      <SelectValue placeholder="Seleziona fornitore" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>URL Feed *</Label>
                  <Input
                    value={feedUrl}
                    onChange={(e) => setFeedUrl(e.target.value)}
                    placeholder="https://esempio.com/products.csv"
                    data-testid="feed-url-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tipo Feed</Label>
                  <Select value={feedType} onValueChange={setFeedType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="xml">XML</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handlePreview}
                    disabled={previewLoading || !feedUrl}
                    className="flex-1"
                    data-testid="preview-btn"
                  >
                    {previewLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                    Anteprima
                  </Button>
                  <Button
                    onClick={handleAutoImport}
                    disabled={importing || !feedUrl || !selectedSupplier}
                    className="flex-1"
                    data-testid="auto-import-btn"
                  >
                    {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                    Auto-Import
                  </Button>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 text-sm">
                  <p className="font-medium mb-2">Come funziona:</p>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Analizza ogni prodotto con scoring qualità/trend/margine</li>
                    <li>• Seleziona: {config?.top_products_percent || 40}% TOP, {config?.trend_products_percent || 35}% TREND, {config?.budget_products_percent || 25}% BUDGET</li>
                    <li>• Target: minimo {config?.min_products || 2000} prodotti</li>
                    <li>• Genera automaticamente titoli AI per i primi 50</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Preview Results */}
            <Card>
              <CardHeader>
                <CardTitle>Anteprima Feed</CardTitle>
              </CardHeader>
              <CardContent>
                {previewData ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-muted/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{previewData.total_in_feed}</p>
                        <p className="text-xs text-muted-foreground">Nel Feed</p>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold">{previewData.stats?.meets_quality_criteria || 0}</p>
                        <p className="text-xs text-muted-foreground">Qualificati</p>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <Badge className="bg-amber-100 text-amber-700">
                        <Star className="h-3 w-3 mr-1" />
                        TOP: {previewData.stats?.top_products || 0}
                      </Badge>
                      <Badge className="bg-purple-100 text-purple-700">
                        <TrendingUp className="h-3 w-3 mr-1" />
                        TREND: {previewData.stats?.trend_products || 0}
                      </Badge>
                      <Badge className="bg-green-100 text-green-700">
                        <DollarSign className="h-3 w-3 mr-1" />
                        BUDGET: {previewData.stats?.budget_products || 0}
                      </Badge>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {previewData.products?.map((p, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">{p.title}</p>
                            <p className="text-xs text-muted-foreground">€{p.cost?.toFixed(2)} | Stock: {p.stock}</p>
                          </div>
                          <Badge className={getTypeColor(p.scores?.product_type)}>
                            {p.scores?.product_type?.toUpperCase()}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Inserisci un URL e clicca "Anteprima"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sources Tab */}
        <TabsContent value="sources" className="space-y-6">
          <div className="flex justify-between items-center">
            <p className="text-muted-foreground">Fonti salvate per import automatici programmati</p>
            <Button onClick={() => setSourceOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Aggiungi Fonte
            </Button>
          </div>

          {sources.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border">
              <p className="text-muted-foreground">Nessuna fonte configurata</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sources.map(source => (
                <Card key={source.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold">{source.name}</h3>
                        <Badge variant="outline">{source.source_type.toUpperCase()}</Badge>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteSource(source.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mb-2">{source.url}</p>
                    {source.last_sync && (
                      <p className="text-xs text-muted-foreground">
                        Ultimo sync: {new Date(source.last_sync).toLocaleDateString('it-IT')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          {jobs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border">
              <p className="text-muted-foreground">Nessun import eseguito</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map(job => (
                <Card key={job.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {job.status === 'completed' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : job.status === 'failed' ? (
                          <AlertCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        )}
                        <div>
                          <p className="font-medium">Job #{job.id.slice(0, 8)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(job.started_at).toLocaleString('it-IT')}
                          </p>
                        </div>
                      </div>
                      {job.result && (
                        <div className="text-right">
                          <p className="font-bold">{job.result.imported} importati</p>
                          <p className="text-xs text-muted-foreground">
                            su {job.result.total_found} trovati
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Configurazione Discovery</DialogTitle>
          </DialogHeader>
          {editConfig && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Prodotti Minimi Target</Label>
                <Input
                  type="number"
                  value={editConfig.min_products}
                  onChange={(e) => setEditConfig(prev => ({ ...prev, min_products: parseInt(e.target.value) }))}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>% TOP</Label>
                  <Input
                    type="number"
                    value={editConfig.top_products_percent}
                    onChange={(e) => setEditConfig(prev => ({ ...prev, top_products_percent: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>% TREND</Label>
                  <Input
                    type="number"
                    value={editConfig.trend_products_percent}
                    onChange={(e) => setEditConfig(prev => ({ ...prev, trend_products_percent: parseInt(e.target.value) }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>% BUDGET</Label>
                  <Input
                    type="number"
                    value={editConfig.budget_products_percent}
                    onChange={(e) => setEditConfig(prev => ({ ...prev, budget_products_percent: parseInt(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Score Qualità Minimo (1-10)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="1"
                  max="10"
                  value={editConfig.min_quality_score}
                  onChange={(e) => setEditConfig(prev => ({ ...prev, min_quality_score: parseFloat(e.target.value) }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Costo Fornitore Massimo (€)</Label>
                <Input
                  type="number"
                  value={editConfig.max_supplier_cost}
                  onChange={(e) => setEditConfig(prev => ({ ...prev, max_supplier_cost: parseFloat(e.target.value) }))}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveConfig}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Source Dialog */}
      <Dialog open={sourceOpen} onOpenChange={setSourceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Fonte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={newSource.name}
                onChange={(e) => setNewSource(prev => ({ ...prev, name: e.target.value }))}
                placeholder="es. Fornitore ABC"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={newSource.source_type} onValueChange={(v) => setNewSource(prev => ({ ...prev, source_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xml">XML</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>URL Feed</Label>
              <Input
                value={newSource.url}
                onChange={(e) => setNewSource(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSourceOpen(false)}>Annulla</Button>
            <Button onClick={handleAddSource}>Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDiscovery;
